/**
 * Consumo del stream SSE del NOC — `GET /api/v1/noc/stream` (ADR 0027).
 *
 * Decisiones respetadas del ADR:
 * - **NO se usa `EventSource`** (no permite fijar cabeceras): el stream se consume con
 *   `fetch` + `Authorization: Bearer <JWT>` y se lee el cuerpo como flujo de texto.
 * - **El token NO viaja en la URL.** Nada de query params.
 * - **Reconexión** con `Last-Event-ID`, que el backend usa para retomar el stream.
 *
 * Mientras el endpoint no exista, el primer fallo marca el stream como no disponible y
 * el hook `useNocLive` continúa con polling cada 3 s.
 */
import { API_BASE_URL } from '@/lib/env';
import { getAccessTokenSnapshot } from '@/lib/http/apiClient';

const STREAM_PATH = '/api/v1/noc/stream';
const MAX_BACKOFF_MS = 15000;

/** Evento SSE crudo ya parseado. */
export interface NocStreamEvent {
  /** `id:` del evento, para `Last-Event-ID`. */
  id?: string;
  /** `event:` (por defecto `message`). */
  event: string;
  /** `data:` concatenado. */
  data: string;
}

export interface NocStreamHandlers {
  signal: AbortSignal;
  /** El stream conectó (a partir de aquí se puede pausar el polling). */
  onOpen?: () => void;
  onEvent: (event: NocStreamEvent) => void;
  /**
   * Ante un `401`, intenta renovar la sesión. Devuelve `true` si hay un token nuevo
   * (el cliente reintenta el stream) o `false` para deshabilitar el SSE y caer a polling.
   */
  onUnauthorized?: () => Promise<boolean>;
  /** El stream no está disponible (404/red/auth no recuperable): dejar el polling. */
  onUnavailable?: (error: unknown) => void;
}

/**
 * Parsea el buffer SSE acumulado. Devuelve los eventos completos, el remanente
 * (un evento puede llegar partido entre chunks) y el último `id:` visto — que debe
 * actualizar `Last-Event-ID` aunque el bloque no traiga `data:` (spec SSE).
 * Soporta `\n` y `\r\n`.
 */
export function parseSseChunk(raw: string): {
  events: NocStreamEvent[];
  rest: string;
  lastId?: string;
} {
  const normalized = raw.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');
  const rest = blocks.pop() ?? '';
  const events: NocStreamEvent[] = [];
  let lastId: string | undefined;

  for (const block of blocks) {
    let id: string | undefined;
    let eventName = 'message';
    const dataLines: string[] = [];

    for (const line of block.split('\n')) {
      if (line === '' || line.startsWith(':')) {
        continue;
      }
      const separator = line.indexOf(':');
      const field = separator === -1 ? line : line.slice(0, separator);
      const value = separator === -1 ? '' : line.slice(separator + 1).replace(/^ /, '');

      if (field === 'id') {
        id = value;
        // El id del stream se conserva aunque el bloque no tenga `data:`.
        lastId = value;
      } else if (field === 'event') {
        eventName = value;
      } else if (field === 'data') {
        dataLines.push(value);
      }
    }

    if (dataLines.length > 0) {
      events.push({ id, event: eventName, data: dataLines.join('\n') });
    }
  }

  return { events, rest, lastId };
}

function backoffDelay(attempt: number): number {
  return Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempt - 1, 4));
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
  });
}

function buildHeaders(lastEventId: string | undefined): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  const token = getAccessTokenSnapshot();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (lastEventId) {
    // Estándar SSE: el backend retoma desde el último evento entregado.
    headers['Last-Event-ID'] = lastEventId;
  }
  return headers;
}

/**
 * Consume el stream hasta que se aborta la `signal`. Si el endpoint no existe, llama a
 * `onUnavailable` y termina (no reintenta en bucle); si ya estaba conectado y se corta,
 * reintenta con backoff exponencial y `Last-Event-ID`. Ante `401` intenta renovar la
 * sesión una sola vez (`onUnauthorized`) antes de deshabilitar el SSE.
 */
export async function consumeNocStream({
  signal,
  onOpen,
  onEvent,
  onUnauthorized,
  onUnavailable,
}: NocStreamHandlers): Promise<void> {
  let lastEventId: string | undefined;
  let hasOpened = false;
  let attempt = 0;
  let authRetried = false;

  while (!signal.aborted) {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}${STREAM_PATH}`, {
        method: 'GET',
        headers: buildHeaders(lastEventId),
        credentials: 'include',
        signal,
      });
    } catch (error) {
      if (signal.aborted) {
        return;
      }
      if (!hasOpened) {
        onUnavailable?.(error);
        return;
      }
      attempt += 1;
      await sleep(backoffDelay(attempt), signal);
      continue;
    }

    // 401: la sesión expiró. Renovar una vez y reintentar el stream con el token nuevo.
    if (response.status === 401) {
      if (!authRetried && onUnauthorized) {
        authRetried = true;
        const renewed = await onUnauthorized().catch(() => false);
        if (renewed && !signal.aborted) {
          continue;
        }
      }
      onUnavailable?.(new Error('Sesión expirada: no se pudo reanudar el stream'));
      return;
    }

    if (!response.ok || !response.body) {
      onUnavailable?.(new Error(`Stream no disponible (HTTP ${response.status})`));
      return;
    }

    hasOpened = true;
    attempt = 0;
    authRetried = false;
    onOpen?.();

    try {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (!signal.aborted) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSseChunk(buffer);
        buffer = parsed.rest;
        // `id:` sin `data:` también actualiza el último id (spec SSE).
        if (parsed.lastId) {
          lastEventId = parsed.lastId;
        }
        for (const event of parsed.events) {
          if (event.id) {
            lastEventId = event.id;
          }
          onEvent(event);
        }
      }
    } catch {
      // Caída del stream: se cae a la reconexión de abajo.
    }

    if (signal.aborted) {
      return;
    }
    attempt += 1;
    await sleep(backoffDelay(attempt), signal);
  }
}
